import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { AnimationClip } from 'three';
import { AppState, Message, MorphTargetDictionary } from '../types';

interface AppStore extends AppState {
  // Audio actions
  setAudioSource: (source: string | null) => void;
  setPlaying: (playing: boolean) => void;
  setLoading: (loading: boolean) => void;
  
  // Avatar actions
  setSpeak: (speak: boolean) => void;
  setClips: (clips: AnimationClip[]) => void;
  setMorphTargetDictionaryBody: (dict: MorphTargetDictionary | null) => void;
  setMorphTargetDictionaryLowerTeeth: (dict: MorphTargetDictionary | null) => void;
  
  // Recording actions
  setRecording: (recording: boolean) => void;
  setAudio: (audio: Blob | null) => void;
  setEndRecording: (endRecording: boolean) => void;
  
  // App actions
  setText: (text: string) => void;
  setChatId: (chatId: string) => void;
  setTenantId: (tenantId: string) => void;
  
  // Chat actions
  addMessage: (message: Omit<Message, 'id' | 'created_at'>) => void;
  clearMessages: () => void;
  
  // Reset actions
  resetAudio: () => void;
  resetRecording: () => void;
}

const initialState: Omit<AppState, 'tenantId'> & { tenantId: string } = {
  text: '',
  chatId: '',
  tenantId: '',
  audio: {
    source: null,
    playing: false,
    loading: false,
  },
  avatar: {
    speak: false,
    clips: [],
    morphTargetDictionaryBody: null,
    morphTargetDictionaryLowerTeeth: null,
  },
  recording: {
    recording: false,
    audio: null,
    endRecording: false,
  },
  messages: [],
};

export const useAppStore = create<AppStore>()(
  devtools(
    (set, get) => ({
      ...initialState,
      
      // Audio actions
      setAudioSource: (source) =>
        set((state) => ({
          audio: { ...state.audio, source }
        })),
      
      setPlaying: (playing) =>
        set((state) => ({
          audio: { ...state.audio, playing }
        })),
      
      setLoading: (loading) =>
        set((state) => ({
          audio: { ...state.audio, loading }
        })),
      
      // Avatar actions
      setSpeak: (speak) =>
        set((state) => ({
          avatar: { ...state.avatar, speak }
        })),
      
      setClips: (clips) =>
        set((state) => ({
          avatar: { ...state.avatar, clips }
        })),
      
      setMorphTargetDictionaryBody: (dict) =>
        set((state) => ({
          avatar: { ...state.avatar, morphTargetDictionaryBody: dict }
        })),
      
      setMorphTargetDictionaryLowerTeeth: (dict) =>
        set((state) => ({
          avatar: { ...state.avatar, morphTargetDictionaryLowerTeeth: dict }
        })),
      
      // Recording actions
      setRecording: (recording) =>
        set((state) => ({
          recording: { ...state.recording, recording }
        })),
      
      setAudio: (audio) =>
        set((state) => ({
          recording: { ...state.recording, audio }
        })),
      
      setEndRecording: (endRecording) =>
        set((state) => ({
          recording: { ...state.recording, endRecording }
        })),
      
      // App actions
      setText: (text) => set({ text }),
      setChatId: (chatId) => set({ chatId }),
      setTenantId: (tenantId) => set({ tenantId }),
      
      // Chat actions
      addMessage: (message) => {
        const newMessage: Message = {
          ...message,
          id: Date.now().toString(),
          created_at: new Date(),
        };
        set((state) => ({
          messages: [...state.messages, newMessage]
        }));
      },
      
      clearMessages: () => set({ messages: [] }),
      
      // Reset actions
      resetAudio: () =>
        set(() => ({
          audio: initialState.audio
        })),
      
      resetRecording: () =>
        set(() => ({
          recording: initialState.recording
        })),
    }),
    {
      name: 'app-store',
    }
  )
);
